import { Button, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import React from "react";
import Modal, { CommonModalProps } from "..";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { GetSectionIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../Redux/redux-store";
import { useTranslation } from "react-i18next";

export type ModalConfirmActionElementProps = {
  displayComment?: boolean;
  commentLabel?: string;
  title?: string;
  switchButtonDescription?: string;
  width?: number;
  canConfirmWithoutComment?: boolean;

  onConfirm?: (comment?: string, isChecked?: boolean) => void;
};

export type ModalConfirmActionProps = CommonModalProps<ModalConfirmActionElementProps>;

const ModalConfirmAction = React.forwardRef<any, ModalConfirmActionProps>((props, ref) => {
  const { closeModal, onConfirm, ...modalProps } = props;
  const {
    displayComment = false,
    commentLabel,
    title,
    width,
    switchButtonDescription,
    canConfirmWithoutComment,
  } = modalProps;
  const [commentValue, setCommentValue] = React.useState<string>();
  const [isChecked, setIsChecked] = React.useState<boolean>(false);
  const isValid = useAppSelector(GetSectionIsValid("form-confirm", "sectionComment"));
  const { t } = useTranslation();

  const updateInputValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCommentValue(event.target.value);
  };

  const handleConfirmClick = () => {
    onConfirm && onConfirm(commentValue, isChecked);
    closeModal();
  };

  const handleCancelClick = () => {
    closeModal();
  };

  return (
    <Modal
      title={title}
      reference={ref}
      closeModal={handleCancelClick}
      width={width ? width : 600}
      footer={
        (displayComment || switchButtonDescription !== undefined) && (
          <Stack direction="row" justifyContent="space-between">
            <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
            <Button
              color="success"
              variant="outlined"
              onClick={handleConfirmClick}
              // TODO: change this by passing it by props and put a default ?
              disabled={displayComment && !isValid && !canConfirmWithoutComment}
            >
              {t("common:confirm")}
            </Button>
          </Stack>
        )
      }
    >
      {displayComment || switchButtonDescription !== undefined ? (
        <Stack spacing={1}>
          {displayComment && (
            <Typography variant="h6" component="span" color="text.secondary">
              {commentLabel !== undefined && commentLabel.length > 0 ? commentLabel : t("field:label.commentColon")}
            </Typography>
          )}
          {displayComment && (
            <Stack spacing={2} mb={2}>
              <FormInputText
                multiline
                value={commentValue}
                onChange={updateInputValue}
                placeholder={t("field:placeholder.comment")}
                rows={3}
                validationProps={{
                  formId: "form-confirm",
                  formSectionId: "sectionComment",
                  fieldId: "comment",
                  initialState: false,
                  required: !canConfirmWithoutComment,
                  minLength: canConfirmWithoutComment ? 0 : 3,
                }}
              />
            </Stack>
          )}
          {switchButtonDescription !== undefined && (
            <Stack spacing={2}>
              <FormControlLabel
                sx={{ mt: 1.5, ml: 0 }}
                control={<Switch size="small" checked={isChecked} onChange={(e) => setIsChecked(e.target.checked)} />}
                label={switchButtonDescription}
              />
            </Stack>
          )}
        </Stack>
      ) : (
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button
            color="success"
            variant="outlined"
            onClick={handleConfirmClick}
            // TODO: change this by passing it by props and put a default ?
            disabled={displayComment && !isValid && !canConfirmWithoutComment}
          >
            {t("common:confirm")}
          </Button>
        </Stack>
      )}
    </Modal>
  );
});

export default ModalConfirmAction;
